import React, { useMemo } from 'react';
import {
  type ButtonType,
  type DataTableRenderCellParams,
  type DataTableRowEventParams,
  type ErpInputTableActions,
  ErpInputTableV2
} from '@/components/custom/erp';
import type { GiaBanDetailRow } from '@/types/schemas';
import { SelectedCellInfo } from './useInputTableRow';
import { getInputTableColumns } from './cols';
import type { FormMode } from '@/types/form';

interface InputTableTabProps {
  mode: FormMode;
  rows: GiaBanDetailRow[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: (params: DataTableRowEventParams<GiaBanDetailRow>) => void;
  onCellClick?: (params: DataTableRenderCellParams<GiaBanDetailRow>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: () => void;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: unknown) => void;
}

const InputTableTab: React.FC<InputTableTabProps> = ({
  mode,
  rows,
  selectedRowUuid,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange
}) => {
  const actions = useMemo<ErpInputTableActions>(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      export: () => {},
      pin: () => {}
    }),
    [onAddRow, onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  const actionOrder = useMemo<ButtonType[]>(
    () => ['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin'],
    []
  );

  return (
    <ErpInputTableV2
      rows={rows}
      className='h-64'
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={getInputTableColumns(onCellValueChange)}
      getRowId={row => row?.uuid || ''}
      actions={actions}
      actionOrder={actionOrder}
      actionsDisabled={mode === 'view'}
      showIndexColumn
    />
  );
};

export default InputTableTab;
